import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { api, describeError } from "@/lib/api";
import { TID } from "@/lib/testIds";
import StepHeader from "./StepHeader";

export default function ClientsStep() {
  const qc = useQueryClient();
  const [desc, setDesc] = useState("");
  const [cid, setCid] = useState("");

  const { data: clients = [], isLoading } = useQuery({
    queryKey: ["clients"],
    queryFn: async () => (await api.get("/api/edi/note-to-claim/clients")).data?.clients ?? [],
  });

  const createMut = useMutation({
    mutationFn: async (payload) =>
      (await api.post("/api/edi/note-to-claim/clients", payload)).data,
    onSuccess: () => {
      toast.success("Client created");
      setDesc("");
      setCid("");
      qc.invalidateQueries({ queryKey: ["clients"] });
    },
    onError: (e) => {
      if (e?.response?.status === 409) toast.error(`Client ID ${cid} is already taken — pick a different 4-digit ID.`);
      else toast.error(describeError(e));
    },
  });

  const delMut = useMutation({
    mutationFn: async (clientId) =>
      (await api.delete(`/api/edi/note-to-claim/clients/${clientId}`)).data,
    onSuccess: () => {
      toast.success("Client removed");
      qc.invalidateQueries({ queryKey: ["clients"] });
    },
    onError: (e) => toast.error(describeError(e)),
  });

  const cidValid = /^\d{4}$/.test(cid.trim());
  const canSubmit = cidValid && !!desc.trim() && !createMut.isPending;

  const onAdd = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    const name = desc.trim();
    createMut.mutate({ client_id: cid.trim(), name, description: name });
  };

  return (
    <div className="flex flex-col gap-0 h-full fade-up">
      <StepHeader
        eyebrow="Account · Step 4 of 5"
        title="Clients"
        subtitle="A client is the entity you bill on behalf of. Give it a 4-digit Client ID and a name — you'll link providers to it next."
        done={clients.length > 0}
      />

      <form onSubmit={onAdd} className="mt-6 flex items-end gap-4">
        <div className="flex flex-col gap-0.5 w-[120px]">
          <label className="lf-label">Client ID</label>
          <input
            className="lf-input font-mono tracking-wide"
            placeholder="e.g. 1000"
            value={cid}
            inputMode="numeric"
            maxLength={4}
            data-testid="client-id-input"
            onChange={(e) => setCid(e.target.value.replace(/\D/g, "").slice(0, 4))}
            required
          />
        </div>
        <div className="flex flex-col gap-0.5 flex-1">
          <label className="lf-label">Name</label>
          <input
            className="lf-input"
            placeholder="e.g. Queens Medical Assoc., Internal Medicine"
            value={desc}
            data-testid={TID.clientDescriptionInput}
            onChange={(e) => setDesc(e.target.value)}
            required
          />
        </div>
        <button
          type="submit"
          disabled={!canSubmit}
          data-testid={TID.clientAddBtn}
          className="btn-ink inline-flex items-center gap-1.5"
        >
          {createMut.isPending ? <Loader2 size={13} className="animate-spin" /> : <Plus size={14} />}
          Add client
        </button>
      </form>
      {cid && !cidValid ? (
        <div className="mt-1.5 text-[11px] text-amber-700">Client ID must be exactly 4 digits.</div>
      ) : null}

      <div className="mt-7 flex-1">
        <div className="text-[10px] font-bold tracking-[0.1em] uppercase text-[var(--gray2)] pb-2 border-b border-black/[.1] flex items-center justify-between">
          <span>Your clients</span>
          <span className="text-[var(--gray3)]">{clients.length} total</span>
        </div>

        {isLoading ? (
          <div className="py-10 text-center text-[12px] text-[var(--gray2)]">Loading clients…</div>
        ) : clients.length === 0 ? (
          <div className="py-10 text-center text-[12px] text-[var(--gray2)] bg-white/40 border border-dashed border-black/[.12] rounded-[14px] mt-3">
            <strong className="text-[var(--ink2)]">No clients yet.</strong> Add your first one above —
            choose a 4-digit Client ID and a name.
          </div>
        ) : (
          <div data-testid={TID.clientsList} className="mt-3 rounded-[14px] overflow-hidden border border-black/[.08] bg-white/55">
            {clients.map((c, i) => (
              <div
                key={c.id}
                data-testid={TID.clientRow(c.client_id || c.id)}
                className={`flex items-center gap-3 px-4 py-3 ${i ? "border-t border-black/[.06]" : ""}`}
              >
                <div className="w-10 h-10 rounded-[10px] bg-[var(--ink)] text-white font-bold text-[12px] flex items-center justify-center flex-shrink-0 tracking-tight">
                  {c.client_id || "—"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-[var(--ink)] truncate">
                    {c.description || c.name || "Untitled client"}
                  </div>
                  <div className="text-[10.5px] text-[var(--gray2)] mt-0.5">
                    {c.linked_npi_count || 0} provider{(c.linked_npi_count || 0) === 1 ? "" : "s"} linked
                    {c.roster_count ? ` · ${c.roster_count} roster rows` : ""}
                  </div>
                </div>
                <button
                  type="button"
                  disabled={delMut.isPending}
                  onClick={() => {
                    if (window.confirm(`Remove client ${c.client_id} (${c.description || c.name || ""})?`))
                      delMut.mutate(c.client_id);
                  }}
                  className="text-[var(--gray3)] hover:text-[var(--amber)] transition p-1.5 rounded-md hover:bg-black/[.04]"
                  aria-label="Remove client"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
